import * as React from 'react'
import {
  CodeSnippet,
  CodeSnippetActions,
  CodeSnippetContainer,
} from '~/sotano-kit/primitives/code-snippet'
import { terminalTheme } from '~/sotano-kit/primitives/bshb-theme'
import { Language, PrismTheme } from 'prism-react-renderer'
import {
  Framework,
  Installer,
  useConnectStore,
} from '~/context/use-connect-tabs'
import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { handleCopyPkCloak, pkCloak } from '../pk-cloak'
import { useInferredGqlQuery } from '../../../../_components/use-inferred-gql-query'
import {
  addSpacesToCodeBlock,
  exampleQuery,
  parseInferredQuery,
} from './framework-example-queries'
import { InlineCode } from '~/sotano-kit/primitives/inline-code'
import { READMEStepper } from '../../../../branch/_components/timeline/steps'
// import { ConnectPrompt } from '../prompt'
import { PageIcon } from '~/components/icons/page'
import { TerminalIcon } from '~/components/icons/terminal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { ExampleActions } from '../../../../_components/example-actions'

export const ModeSDK = ({
  token,
  teamSlug,
  repoSlug,
  commitId,
}: {
  token: string
  teamSlug: string
  repoSlug: string
  commitId: string
}) => {
  const envCodeToCopy = `BASEHUB_TOKEN="${token}"`
  const envCode = `BASEHUB_TOKEN="${pkCloak}"

# optional

BASEHUB_DRAFT=<true|false> # defaults to false
BASEHUB_REF=<branch-name|commit-id> # defaults to your default branch`

  const [connectPreferences, setConnectPreferences] = useConnectStore()

  const { data: inferredGqlQuery } = useInferredGqlQuery({
    token,
    teamSlug,
    repoSlug,
    commitId,
  })

  return (
    <READMEStepper
      steps={[
        {
          title: (
            <>
              Install the <InlineCode>basehub</InlineCode> SDK
            </>
          ),
          subtitle: (
            <>
              Used to fetch data from BaseHub. It includes utils that help you
              display it.
            </>
          ),
          children: (
            <ConnectCodeSnippet
              snippets={STEPS.install}
              theme={terminalTheme}
              value={connectPreferences.installer}
              handleValueChange={(v) =>
                setConnectPreferences((p) => ({
                  ...(p as any),
                  installer: v,
                }))
              }
            />
          ),
        },
        {
          title: 'Configure',
          subtitle: 'Set required environment variables.',
          children: (
            <CodeSnippetContainer>
              <CodeSnippetActions label=".env" icon={<PageIcon />} />
              <CodeSnippet
                code={envCode}
                copyContentOverride={envCodeToCopy}
                onCopy={(e) => handleCopyPkCloak(e, token)}
                language="bash"
                lineNumbers
                className="p-4 overflow-x-hidden light:bg-dark-base"
                unstyled
                theme={terminalTheme}
              />
            </CodeSnippetContainer>
          ),
        },
        {
          title: 'Generate',
          subtitle: (
            <>
              Use the <InlineCode>basehub</InlineCode> script to generate a
              type-safe SDK for your app.
            </>
          ),
          children: (
            <>
              <ConnectCodeSnippet
                snippets={STEPS.generate}
                value={connectPreferences.installer}
                handleValueChange={(v) =>
                  setConnectPreferences((p) => ({
                    ...(p as any),
                    installer: v,
                  }))
                }
                theme={terminalTheme}
              />
              <p className="my-6 max-w-[600px] text-sm leading-[19px] text-dark-muted">
                <b>
                  <ExclamationTriangleIcon className="inline-block mr-1" />
                  Important:{' '}
                </b>
                Make sure you run the generator before your app's build step.
                The recommended setup for Next.js looks like this:
              </p>
              <CodeSnippetContainer>
                <ExampleActions
                  label="package.json"
                  snippetToCopy={packageJSON}
                />
                <CodeSnippet
                  code={packageJSON}
                  language="json"
                  lineNumbers
                  className="p-4 light:bg-dark-base"
                  unstyled
                  theme={terminalTheme}
                />
              </CodeSnippetContainer>
            </>
          ),
        },
        {
          title: 'Integrate',
          subtitle: (
            <>
              Import it into your app and retrieve your data without worrying
              about type definitions.
            </>
          ),
          children: (
            <>
              {connectPreferences.framework === 'SvelteKit' ? (
                <p className="my-6 text-sm leading-[19px] text-dark-muted">
                  <b>
                    <ExclamationTriangleIcon className="inline-block mr-1" />
                    Important:{' '}
                  </b>
                  SvelteKit doesn't automatically load environment variables to{' '}
                  <InlineCode>process.env</InlineCode>.<br />
                  Refer to{' '}
                  <ButtonLink
                    href="https://kit.svelte.dev/docs/adapter-node#environment-variables"
                    target="_blank"
                    unstyled
                    className="underline transition-colors hover:text-dark-dim"
                  >
                    the SvelteKit docs
                  </ButtonLink>{' '}
                  to do so.
                </p>
              ) : null}
              {/* {inferredGqlQuery ? (
          <p className="max-w-[600px] flex items-center text-sm leading-[19px] text-dark-muted">
            <CheckCircledIcon className="inline-block mr-1" />
            This example query was inferred from your schema.
          </p>
        ) : null} */}
              <ConnectCodeSnippet
                snippets={STEPS.integrate}
                value={connectPreferences.framework}
                handleValueChange={(v) =>
                  setConnectPreferences((p) => ({
                    ...(p as any),
                    framework: v,
                  }))
                }
                snippetArgs={
                  inferredGqlQuery?.result
                    ? [parseInferredQuery(inferredGqlQuery.result)]
                    : undefined
                }
                lineNumbers
              />
            </>
          ),
        },
      ]}
    />
  )
}

export const ConnectCodeSnippet = <T extends Framework | Installer>({
  snippets,
  value,
  handleValueChange,
  snippetArgs = [],
  ...rest
}: {
  value: T
  handleValueChange: (value: T) => void
  snippets: StepExampleOption[]
  theme?: PrismTheme
  lineNumbers?: boolean
  snippetArgs?: string[]
}) => {
  const data = React.useMemo(
    () => snippets.find((item) => item.selectValue === value),
    [value, snippets]
  )

  if (!data) return null
  if (data.snippet instanceof Array) {
    return (
      <>
        {data.snippet.map((snippet) => {
          const label = snippet.filename ?? 'Terminal'

          return (
            <CodeSnippetContainer key={snippet.filename} className="mb-4">
              <CodeSnippetActions
                label={label}
                icon={label === 'Terminal' ? <TerminalIcon /> : <PageIcon />}
              >
                <Select value={value} onValueChange={handleValueChange}>
                  <SelectTrigger
                    aria-label="language"
                    size="small"
                    iconRight
                    color="transparent"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {snippets.map((snippet) => (
                      <SelectItem
                        key={snippet.selectValue}
                        value={snippet.selectValue}
                        disabled={snippet.disabled}
                      >
                        {snippet.selectValue}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CodeSnippetActions>
              <CodeSnippet
                {...rest}
                code={
                  typeof snippet.code === 'string'
                    ? snippet.code
                    : snippet.code(...snippetArgs)
                }
                language={data.language ?? 'bash'}
                className="p-4 light:bg-dark-base"
                unstyled
              />
            </CodeSnippetContainer>
          )
        })}
      </>
    )
  }

  const label = data.snippet.filename ?? 'Terminal'

  return (
    <CodeSnippetContainer>
      <CodeSnippetActions
        label={label}
        icon={label === 'Terminal' ? <TerminalIcon /> : <PageIcon />}
      >
        <Select value={value} onValueChange={handleValueChange}>
          <SelectTrigger aria-label="language" color="transparent">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {snippets.map((snippet) => (
              <SelectItem
                key={snippet.selectValue}
                value={snippet.selectValue}
                disabled={snippet.disabled}
              >
                {snippet.selectValue}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CodeSnippetActions>
      <CodeSnippet
        {...rest}
        code={
          typeof data.snippet.code === 'string'
            ? data.snippet.code
            : data.snippet.code(...snippetArgs)
        }
        language={data.language ?? 'bash'}
        className="p-4 light:bg-dark-base"
        unstyled
      />
    </CodeSnippetContainer>
  )
}

export type StepExampleOption = {
  selectValue: Framework | Installer
  language?: Language
  snippet: Snippet | Snippet[]
  disabled?: boolean
}

export type Snippet = {
  filename?: string
  code: string | ((...args: string[]) => string)
}

const STEPS = {
  install: [
    {
      selectValue: 'npm',
      snippet: { code: 'npm install basehub' },
    },
    {
      selectValue: 'yarn',
      snippet: { code: 'yarn add basehub' },
    },
    {
      selectValue: 'pnpm',
      snippet: { code: 'pnpm add basehub' },
    },
  ] satisfies StepExampleOption[],
  generate: [
    {
      selectValue: 'npm',
      snippet: { code: 'npx basehub' },
    },
    {
      selectValue: 'yarn',
      snippet: { code: 'yarn basehub' },
    },
    {
      selectValue: 'pnpm',
      snippet: { code: 'pnpm basehub' },
    },
  ] satisfies StepExampleOption[],
  integrate: [
    {
      selectValue: 'Next.js',
      language: 'tsx',
      snippet: {
        filename: 'app/page.tsx',
        code: () => `import { Pump } from 'basehub/react-pump'

const Page = async () => {
  return (
    <Pump queries={[{ __typename: true }]}>
      {async ([data]) => {
        'use server'
        return <>{JSON.stringify(data, null, 2)}</>
      }}
    </Pump>
  )
}

export default Page`,
      },
    },
    {
      selectValue: 'Astro',
      language: 'tsx',
      snippet: {
        filename: 'src/pages/blog/index.astro',
        code: (recommended) => `---
import { basehub } from "basehub"

${addSpacesToCodeBlock(recommended || exampleQuery, 0)}
---

<!DOCTYPE html>
<html lang="en">
  <head>
    <title>My Website</title>
  </head>
  <body>
    <pre>{JSON.stringify(data, null, 2)}</pre>
  </body>
</html>`,
      },
    },
    {
      selectValue: 'SvelteKit',
      language: 'typescript',
      snippet: [
        {
          filename: 'src/routes/+page.server.ts',
          code: (recommended) => `import type { PageServerLoad } from './$types'
import { basehub } from 'basehub'

export const load: PageServerLoad = async () => {
${addSpacesToCodeBlock(recommended || exampleQuery, 2)}

  return {
    page_server_data: { data }
  }
}`,
        },
        {
          filename: 'src/routes/+page.svelte',
          code: `<script lang="ts">
  import type { PageData } from './$types'
  export let data: PageData
</script>

<h1>Welcome to SvelteKit</h1>
<pre>{JSON.stringify(data, null, 2)}</pre>`,
        },
      ],
    },
    {
      selectValue: 'Remix',
      language: 'javascript',
      snippet: { code: '' },
      disabled: true,
    },
    {
      selectValue: 'Vue.js',
      language: 'javascript',
      snippet: { code: '' },
      disabled: true,
    },
  ] satisfies StepExampleOption[],
}

const packageJSON = `{
  "scripts": {
    "dev": "basehub dev & next dev",
    "build": "basehub && next build",
  }
}`
